// frontend/src/pages/EwpAccessory.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, ListBox } from "../ui/UIComponents";

export default function EwpAccessory() {
  const nav = useNavigate();

  // ---------- Left: list of existing rows ----------
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [errorList, setErrorList] = useState("");

  // ---------- Right: form ----------
  const empty = {
    opt_id: null,   // hidden id (sys_option.opt_id)
    product: "",    // display only
    desc: "",       // display only
    exw: "",        // input "xxxx.xx"
    mode: "new",    // "new" | "edit"
  };
  const [form, setForm] = useState(empty);
  const [initialForm, setInitialForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [errorSave, setErrorSave] = useState("");
  const [hint, setHint] = useState("");

  // ---------- Product picker (public.sys_option) ----------
  const [prodQ, setProdQ] = useState("");
  const [prodOpts, setProdOpts] = useState([]);
  const [prodLoading, setProdLoading] = useState(false);
  const [prodError, setProdError] = useState("");

  const prodMap = useMemo(() => {
    const m = Object.create(null);
    for (const o of prodOpts) m[String(o.opt_id)] = o;
    return m;
  }, [prodOpts]);

  // ---------- Responsive ----------
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth <= 920
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 920);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const qRef = useRef(null);
  const prodQRef = useRef(null);

  // Debounce searches
  useEffect(() => {
    const t = setTimeout(() => loadList(q), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const t = setTimeout(() => loadProdOptions(prodQ), 250);
    return () => clearTimeout(t);
  }, [prodQ]);

  // Initial load
  useEffect(() => {
    loadList("");
    loadProdOptions("");
    setTimeout(() => qRef.current && qRef.current.focus(), 0);
  }, []);

  async function loadList(term) {
    try {
      setLoadingList(true);
      setErrorList("");
      const u = new URL("/api/data", window.location.origin);
      u.searchParams.set("scope", "ewp_accessory");
      u.searchParams.set("sub", "list"); // <-- required by API
      if (term && term.trim()) u.searchParams.set("q", term.trim());
      const r = await fetch(u.toString(), { method: "GET" });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
      setRows(Array.isArray(j.rows) ? j.rows : []);
    } catch (e) {
      setErrorList(String(e.message || e));
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  }

  async function loadProdOptions(term) {
    try {
      setProdLoading(true);
      setProdError("");
      const u = new URL("/api/data", window.location.origin);
      u.searchParams.set("scope", "ewp_accessory");
      u.searchParams.set("sub", "options"); // <-- required by API
      if (term && term.trim()) u.searchParams.set("q", term.trim());
      const r = await fetch(u.toString(), { method: "GET" });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
      setProdOpts(Array.isArray(j.rows) ? j.rows : []);
    } catch (e) {
      setProdError(String(e.message || e));
      setProdOpts([]);
    } finally {
      setProdLoading(false);
    }
  }

  // Select an existing row → edit mode (product locked)
  function selectRow(r) {
    const next = {
      opt_id: r.opt_id ?? null,
      product: r.product ?? "",
      desc: r.desc ?? "",
      exw: toFixed2(r.exw) ?? "",
      mode: "edit",
    };
    setForm(next);
    setInitialForm(next);
    setHint("Selected");
    setErrorSave("");
  }

  // Choose a product from picker (only when mode === "new")
  function onPickProduct(e) {
    if (form.mode === "edit") return;
    const value = e.target.value;
    if (!value) {
      setForm((f) => ({ ...f, opt_id: null, product: "", desc: "" }));
      return;
    }
    const sel = prodMap[value];
    setForm((f) => ({
      ...f,
      opt_id: Number(value),
      product: sel ? sel.product : "",
      desc: sel ? sel["HD Code"] || "" : "",
    }));
  }

  function onChangeExw(e) {
    const v = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(v) || v === "") {
      setForm((f) => ({ ...f, exw: v }));
    }
  }

  function newRow() {
    const next = { ...empty, mode: "new" };
    setForm(next);
    setInitialForm(next);
    setHint("New entry");
    setErrorSave("");
    setTimeout(() => prodQRef.current && prodQRef.current.focus(), 0);
  }

  function resetForm() {
    setForm(initialForm);
    setHint("Reset");
    setErrorSave("");
  }

  const changed = JSON.stringify(form) !== JSON.stringify(initialForm);

  async function onSave() {
    setErrorSave("");
    setHint("");

    if (!form.opt_id) {
      setErrorSave("Please select a Product.");
      return;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(String(form.exw))) {
      setErrorSave("ExWork must be a number like 1234.56");
      return;
    }

    const payload = { opt_id: form.opt_id, exw: Number(form.exw) };

    setSaving(true);
    try {
      let r, j;
      if (form.mode === "edit") {
        r = await fetch("/api/data?scope=ewp_accessory", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload), // { opt_id, exw }
        });
        j = await r.json();
        if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
        // refresh UI with returned row (product, desc, exw, opt_id)
        selectRow(j.row);
        setRows((prev) => prev.map((row) => (row.opt_id === j.row.opt_id ? j.row : row)));
        setHint("Saved changes");
      } else {
        r = await fetch("/api/data?scope=ewp_accessory", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload), // { opt_id, exw }
        });
        j = await r.json();
        if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
        setRows((prev) => [j.row, ...prev]);
        selectRow(j.row);
        setHint("Inserted");
      }
    } catch (e) {
      setErrorSave(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  function back() {
    nav("/purchase/config");
  }

  // ---------- Layout ----------
  const grid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "360px 1fr",
    gap: isNarrow ? 10 : 16,
    alignItems: "start",
  };
  const fieldGrid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "210px 1fr",
    gap: 10,
    alignItems: "center",
  };
  const smallText = { fontSize: isNarrow ? 11 : 12, opacity: 0.75 };

  return (
    <div className="app-page">
      <main className="container" style={isNarrow ? { fontSize: 15 } : undefined}>
        <h1 className="menu-title" style={isNarrow ? { fontSize: 22, lineHeight: "24px" } : undefined}>
          Tuiss Web Tools — Purchase
        </h1>
        <h2 style={{ marginTop: -8, ...(isNarrow ? { fontSize: 16 } : {}) }}>
          Retail Accessory Catalog
        </h2>

        {/* Top buttons */}
        <div style={{ margin: "8px 0 12px", display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Button onClick={back}>← Trở về / Back</Button>
          <Button onClick={newRow}>New</Button>
          <Button onClick={resetForm} disabled={!changed}>Reset</Button>
          <Button onClick={onSave} disabled={saving || (!changed && form.mode === "edit")}>
            {saving ? "Saving..." : "Save"}
          </Button>
          {hint ? <span style={{ marginLeft: 4, ...smallText }}>{hint}</span> : null}
        </div>

        <div style={grid}>
          {/* LEFT: search + results */}
          <section className="box" style={{ overflow: "hidden" }}>
            <label style={{ display: "block", fontWeight: 600, ...(isNarrow ? { fontSize: 14 } : {}) }}>
              Search / Chọn (Product — Desc — ExWork)
            </label>
            <input
              ref={qRef}
              className="text-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="type name or description to search…"
            />
            <div style={{ marginTop: 6, ...smallText }}>
              {loadingList ? "Loading..." : rows.length + " item(s)"}
              {errorList ? <span style={{ color: "crimson", marginLeft: 8 }}>{errorList}</span> : null}
            </div>

            <div
              style={{
                marginTop: 8,
                maxHeight: isNarrow ? 320 : 440,
                overflow: "auto",
                borderTop: "1px solid #eee",
              }}
            >
              {rows.map((r) => {
                const sel = r.opt_id === form.opt_id && form.mode === "edit";
                return (
                  <button
                    key={r.opt_id}
                    onClick={() => selectRow(r)}
                    className="menu-card"
                    style={{
                      width: "100%",
                      display: "block",
                      textAlign: "left",
                      padding: isNarrow ? "10px 10px" : "8px 10px",
                      border: "none",
                      borderBottom: "1px solid #f0f0f0",
                      borderRadius: 0,
                      background: sel ? "rgba(0,0,0,0.05)" : "transparent",
                      fontSize: isNarrow ? 15 : 14,
                    }}
                    title="Select"
                  >
                    <div style={{ fontWeight: 600 }}>
                      {(r.product || "(no name)")} — {(r.desc || "(no desc)")}
                    </div>
                    <div style={{ marginTop: 2, ...smallText }}>
                      {"opt_id #" + r.opt_id} {" • ExWork " + toFixed2(r.exw)}
                    </div>
                  </button>
                );
              })}
              {rows.length === 0 && !loadingList ? (
                <div style={{ padding: 8, ...smallText }}>No results</div>
              ) : null}
            </div>
          </section>

          {/* RIGHT: form */}
          <section className="box" style={{ marginTop: isNarrow ? 12 : 0 }}>
            <div style={fieldGrid}>
              {/* Product picker */}
              <label>Product</label>
              <div>
                <input
                  ref={prodQRef}
                  className="text-input"
                  value={prodQ}
                  onChange={(e) => setProdQ(e.target.value)}
                  placeholder="search product (name or desc)…"
                  disabled={form.mode === "edit"}
                  title={form.mode === "edit" ? "Product is locked in edit mode" : "Type to filter"}
                />
                <div style={{ marginTop: 6 }}>
                  <ListBox
                    multiple={false}
                    size={10}
                    value={form.opt_id ? String(form.opt_id) : ""}
                    onChange={onPickProduct}
                    disabled={form.mode === "edit" || prodLoading || !!prodError}
                  >
                    <option value="">
                      {prodLoading ? "Loading…" : prodError ? "Error loading" : "-- Select product --"}
                    </option>
                    {prodOpts.map((o) => (
                      <option key={o.opt_id} value={String(o.opt_id)} title={o["HD Code"] || ""}>
                        {o.product} — {o["HD Code"] || ""}
                      </option>
                    ))}
                  </ListBox>
                  <div style={{ marginTop: 6, ...smallText }}>
                    {prodLoading ? "Loading options…" : prodError ? <span style={{ color: "crimson" }}>{prodError}</span> : `Options: ${prodOpts.length}`}
                  </div>
                </div>
              </div>

              {/* Read-only selection details */}
              <label>Selected</label>
              <div style={{ fontSize: 14, opacity: 0.9 }}>
                <div><b>Product:</b> {form.product || "-"}</div>
                <div><b>HD Code:</b> {form.desc || "-"}</div>
                <div><b>opt_id:</b> {form.opt_id ?? "-"}</div>
              </div>

              {/* ExWork */}
              <label>ExWork</label>
              <input
                className="text-input"
                name="exw"
                value={form.exw}
                onChange={onChangeExw}
                placeholder="1234.56"
                inputMode="decimal"
              />
            </div>

            {errorSave ? (
              <div style={{ color: "crimson", marginTop: 10 }}>{errorSave}</div>
            ) : (
              <div style={{ marginTop: 10, ...smallText }}>
                {form.mode === "edit"
                  ? "Product is read-only in edit mode; you can only change ExWork."
                  : "Pick a product, then enter ExWork (xxxx.xx)."}
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              <Button onClick={() => nav("/purchase/config/retail-product")}>
                Add new retail accessory
              </Button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

// ---------- helpers ----------
function toFixed2(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}